/**
 * Expression parser for the proof. grammar (Track 2.A / P3.GRAMMAR).
 *
 * Parses expressions starting with `=` in value slots.
 * Produces AST nodes from extensibility-design.md §3.1.
 *
 * Grammar (simplified Pratt / recursive descent):
 *
 *   expr     ::= add_sub
 *   add_sub  ::= mul_div (('+' | '-') mul_div)*
 *   mul_div  ::= power (('*' | '/') power)*
 *   power    ::= unary ('^' unary)?
 *   unary    ::= '-' unary | primary
 *   primary  ::= literal | reference | function_call | aggregation | '(' expr ')'
 *
 *   literal  ::= NUMBER UNIT?
 *   reference::= IDENT ('/' IDENT)* ('.' IDENT)?    -- path + optional subfield
 *              | '@' IDENT '.' IDENT                 -- @section.label
 *              | '..' '/' IDENT                      -- parent scope
 *   function_call ::= IDENT '(' expr (',' expr)* ')'
 *   aggregation ::= AGG_FN '(' target ('where' filter)? (',' field)? ')'
 *
 * Identifier rules (extensibility-design.md §3.4.0 / L13):
 *   - Identifiers allow internal single spaces.
 *   - Terminated by: operator, digit-preceded-by-space, lone-unit-symbol-preceded-by-space,
 *     newline, EOI.
 *   - No implicit multiplication.
 *   - Reserved keywords cannot be used as identifiers.
 *
 * Security: no eval, no new Function. User text never enters a JS execution context.
 *
 * Pure TS — no DOM, no Node built-ins.
 */

import type {
  ExpressionNode,
  LiteralNode,
  ReferenceNode,
  FunctionCallNode,
  BinaryOpNode,
  BinaryOp,
  UnaryMinusNode,
  AggregationNode,
  AggregationFn,
  AggregationFilter,
} from "../ast/types.js";

// ---------------------------------------------------------------------------
// Aggregation function names — also reserved keywords per §3.4.0
// ---------------------------------------------------------------------------

const AGG_FNS = new Set<string>(["sum", "count", "max", "min", "avg", "first", "last"]);

/**
 * Reserved keywords that cannot be used as variable names or labelled-line keys.
 * extensibility-design.md §3.4.0.
 */
const RESERVED_KEYWORDS = new Set<string>([
  "where",
  "pivot",
  "sum",
  "count",
  "max",
  "min",
  "avg",
  "first",
  "last",
  "true",
  "false",
  // Geometry stdlib names
  "cylinder",
  "rect",
  "ring",
  "sphere",
  // Math stdlib
  "log",
  "sqrt",
]);

/**
 * Single-character unit symbols used by the space-termination lookahead.
 * extensibility-design.md §3.4.0 — the set {g, l, m, s, d, K}.
 */
const LONE_UNIT_SYMBOLS = new Set<string>(["g", "l", "m", "s", "d", "K"]);

// ---------------------------------------------------------------------------
// Tokenizer
// ---------------------------------------------------------------------------

type TokKind =
  | "number"
  | "ident"
  | "at-ident" // @foo
  | "dotdot-slash" // ../
  | "plus"
  | "minus"
  | "star"
  | "slash"
  | "caret"
  | "lparen"
  | "rparen"
  | "comma"
  | "dot"
  | "eq"
  | "neq"
  | "lt"
  | "lte"
  | "gt"
  | "gte"
  | "where"
  | "unit"
  | "eof";

interface Tok {
  kind: TokKind;
  text: string;
  pos: number;
}

// Recognised unit symbols for the tokeniser (longest first)
const UNIT_RE =
  /^(tbsp|tsp|floz|cup|lbs|inch|kg|mg|ml|cl|dl|cm³|cm3|cm²|cm2|cm|mm|oz|lb|in|°C|°F|sec|min|hr|g|l|m|s|h|d|K|C|F|%)(?![a-zA-Z])/u;

/**
 * Check if the character at `expr[pos]` is an operator character (terminates an identifier).
 */
function isOperatorChar(ch: string): boolean {
  return (
    ch === "+" ||
    ch === "-" ||
    ch === "*" ||
    ch === "/" ||
    ch === "^" ||
    ch === "(" ||
    ch === ")" ||
    ch === "," ||
    ch === "." ||
    ch === "=" ||
    ch === "!" ||
    ch === "<" ||
    ch === ">" ||
    ch === ":" ||
    ch === "@"
  );
}

/**
 * Determine whether a space at position `spacePos` inside an identifier
 * should terminate the identifier (expression context).
 *
 * Termination signals (extensibility-design.md §3.4.0):
 *   1. Space followed by a digit that starts a STANDALONE number
 *      (digit sequence + optional unit suffix, then non-identifier char).
 *      Example: `water 260g` → `water` terminates because `260g` is standalone.
 *      Counter-example: `Tipo 00 Flour` → `Tipo` does NOT terminate because
 *      `00 Flour` has word-chars after the digit sequence (not standalone).
 *   2. Space followed by a lone unit symbol immediately followed by a
 *      non-identifier character → terminate identifier.
 *   3. Space followed by an operator or colon → terminate identifier.
 *   4. Space at EOI → terminate.
 *
 * Returns true if the space terminates the identifier.
 */
function spaceTerminatesIdent(expr: string, spacePos: number): boolean {
  const next = expr[spacePos + 1];
  if (next === undefined) return true; // EOI
  if (isOperatorChar(next)) return true; // operator → terminate
  if (next === "\n" || next === "\r") return true; // newline → terminate

  // Digit check: only terminate if the digit starts a STANDALONE number.
  //
  // A standalone number is: digit-sequence + optional unit suffix, followed by
  // an operator, colon, newline, or EOI — but NOT by a space+letter (which
  // would indicate more identifier chars follow).
  //
  // Examples:
  //   `water 260g:`  → `260g` followed by `:` → standalone → terminate `water`
  //   `water 260 +`  → `260` followed by ` +` → space then operator → standalone → terminate
  //   `Tipo 00 Flour` → `00` followed by ` Flour` (space+letter) → NOT standalone → continue
  //
  if (/\d/u.test(next)) {
    // Scan forward past the digit sequence + optional decimal
    let j = spacePos + 1;
    while (j < expr.length && /\d/u.test(expr[j] ?? "")) j++;
    if (expr[j] === ".") {
      j++;
      while (j < expr.length && /\d/u.test(expr[j] ?? "")) j++;
    }
    // Optionally a unit suffix (runs of ASCII letters + degree symbols)
    if (j < expr.length && /[a-zA-Z°]/u.test(expr[j] ?? "")) {
      while (j < expr.length && /[a-zA-Z0-9°²³]/u.test(expr[j] ?? "")) j++;
    }
    // What follows the candidate number?
    const afterNum = expr[j];
    if (afterNum === undefined) return true; // EOI → standalone
    if (isOperatorChar(afterNum)) return true; // operator → standalone
    if (afterNum === "\n" || afterNum === "\r") return true; // newline → standalone
    if (afterNum === " ") {
      // Space after number: look one more position to decide
      const afterSpace = expr[j + 1];
      if (
        afterSpace === undefined ||
        isOperatorChar(afterSpace) ||
        /\d/u.test(afterSpace) ||
        afterSpace === "\n" ||
        afterSpace === "\r"
      ) {
        // Space followed by EOI / operator / digit → standalone
        return true;
      }
      // Space followed by a letter → more identifier chars → NOT standalone
      return false;
    }
    // Any other character after number → not standalone
    return false;
  }

  // Lone unit symbol check: next char is in LONE_UNIT_SYMBOLS and the char
  // after it is a non-identifier character (operator, space, newline, or EOI).
  if (LONE_UNIT_SYMBOLS.has(next)) {
    const afterUnit = expr[spacePos + 2];
    if (
      afterUnit === undefined ||
      isOperatorChar(afterUnit) ||
      afterUnit === " " ||
      afterUnit === "\n" ||
      afterUnit === "\r"
    ) {
      return true;
    }
  }

  return false;
}

/**
 * Tokenize an expression string (Mode B — expression context).
 *
 * Implements the P1.B state machine from extensibility-design.md §3.4.0:
 * - Identifiers may contain internal single spaces.
 * - Termination signals: operator, digit-preceded-by-space, lone-unit-symbol-preceded-by-space.
 * - Reserved keywords emitted as their own token kind ('where') or as idents checked upstream.
 * - Implicit-multiplication is NOT supported.
 */
function tokenize(expr: string): Tok[] {
  const toks: Tok[] = [];
  let i = 0;

  while (i < expr.length) {
    // Skip leading whitespace between tokens
    if (expr[i] === " " || expr[i] === "\t") {
      i++;
      continue;
    }

    // Skip newlines (expressions don't span lines; the caller trims)
    if (expr[i] === "\n" || expr[i] === "\r") {
      i++;
      continue;
    }

    const ch = expr[i] ?? "";

    // Operators and punctuation
    if (ch === "+") {
      toks.push({ kind: "plus", text: "+", pos: i });
      i++;
      continue;
    }
    if (ch === "-") {
      toks.push({ kind: "minus", text: "-", pos: i });
      i++;
      continue;
    }
    if (ch === "*") {
      toks.push({ kind: "star", text: "*", pos: i });
      i++;
      continue;
    }
    if (ch === "^") {
      toks.push({ kind: "caret", text: "^", pos: i });
      i++;
      continue;
    }
    if (ch === "(") {
      toks.push({ kind: "lparen", text: "(", pos: i });
      i++;
      continue;
    }
    if (ch === ")") {
      toks.push({ kind: "rparen", text: ")", pos: i });
      i++;
      continue;
    }
    if (ch === ",") {
      toks.push({ kind: "comma", text: ",", pos: i });
      i++;
      continue;
    }
    if (ch === ".") {
      // Check for ../
      if (expr[i + 1] === "." && expr[i + 2] === "/") {
        toks.push({ kind: "dotdot-slash", text: "../", pos: i });
        i += 3;
        continue;
      }
      toks.push({ kind: "dot", text: ".", pos: i });
      i++;
      continue;
    }
    if (ch === "!") {
      if (expr[i + 1] === "=") {
        toks.push({ kind: "neq", text: "!=", pos: i });
        i += 2;
        continue;
      }
    }
    if (ch === "<") {
      if (expr[i + 1] === "=") {
        toks.push({ kind: "lte", text: "<=", pos: i });
        i += 2;
        continue;
      }
      toks.push({ kind: "lt", text: "<", pos: i });
      i++;
      continue;
    }
    if (ch === ">") {
      if (expr[i + 1] === "=") {
        toks.push({ kind: "gte", text: ">=", pos: i });
        i += 2;
        continue;
      }
      toks.push({ kind: "gt", text: ">", pos: i });
      i++;
      continue;
    }
    if (ch === "/") {
      toks.push({ kind: "slash", text: "/", pos: i });
      i++;
      continue;
    }
    if (ch === "=") {
      toks.push({ kind: "eq", text: "=", pos: i });
      i++;
      continue;
    }

    // @ prefix: @ident
    if (ch === "@") {
      // @-idents use the classic word-char-only rule (directive names have no spaces)
      const identRe = /^@([a-zA-Z][a-zA-Z0-9_-]*)/u;
      const m = identRe.exec(expr.slice(i));
      if (m) {
        toks.push({ kind: "at-ident", text: m[0] ?? "", pos: i });
        i += (m[0] ?? "").length;
        continue;
      }
      // Lone @ — treat as prose char; skip
      i++;
      continue;
    }

    // Number (possibly followed immediately by unit)
    if (/\d/u.test(ch)) {
      const numRe = /^\d+(?:\.\d+)?/u;
      const nm = numRe.exec(expr.slice(i));
      if (nm) {
        const numText = nm[0] ?? "";
        i += numText.length;
        // Check for unit immediately after number (no space between num + unit)
        const rest = expr.slice(i);
        const um = UNIT_RE.exec(rest);
        if (um) {
          // Emit number + unit as a single "number" token with unit suffix
          toks.push({ kind: "number", text: numText + (um[0] ?? ""), pos: i - numText.length });
          i += (um[0] ?? "").length;
        } else {
          toks.push({ kind: "number", text: numText, pos: i - numText.length });
        }
        continue;
      }
    }

    // Identifier — Mode B (expression context) with space-aware accumulation.
    // Implements the IDENT state of the P1.B state machine.
    if (/[a-zA-Z_]/u.test(ch)) {
      const startPos = i;
      let raw = "";

      while (i < expr.length) {
        const c = expr[i] ?? "";

        if (/[a-zA-Z0-9_]/u.test(c)) {
          // Normal identifier character — accumulate
          raw += c;
          i++;
        } else if (c === " ") {
          // If we've accumulated a reserved keyword so far, terminate immediately.
          // Reserved keywords like `where` must be emitted as their own token and
          // cannot grow into a multi-word identifier like `where temp`.
          if (RESERVED_KEYWORDS.has(raw.toLowerCase())) {
            break;
          }
          // Space: decide whether to terminate or include
          if (spaceTerminatesIdent(expr, i)) {
            // Terminate — don't consume the space (it belongs between tokens)
            break;
          }
          // Include space as internal identifier character
          raw += " ";
          i++;
        } else if (c === "-" && raw.length > 0) {
          // Hyphen may be part of old-style identifiers (e.g. `shopping-list`).
          // Only allowed mid-ident, not at start or end.
          const after = expr[i + 1];
          if (after !== undefined && /[a-zA-Z0-9]/u.test(after)) {
            raw += c;
            i++;
          } else {
            break;
          }
        } else {
          // Operator, colon, @, newline, etc. — terminate
          break;
        }
      }

      // Trim trailing spaces (spec: trailing space is NOT part of the identifier)
      const text = raw.trimEnd();

      if (text.length === 0) {
        // Empty after trim — skip
        continue;
      }

      // Detect implicit multiplication: if the previous token was a number
      // and this ident immediately follows (no operator between them),
      // that's an implicit-mul error. This is checked at parse level in
      // parsePrimary, not here, because we'd need lookahead over previous tokens.
      // The tokenizer just emits the ident normally; parsePrimary detects adjacency.

      // Classify token
      if (text === "where") {
        toks.push({ kind: "where", text, pos: startPos });
      } else {
        toks.push({ kind: "ident", text, pos: startPos });
      }
      continue;
    }

    // Unicode degree symbols for units: °C, °F
    if (ch === "°") {
      const degRe = /^°[CF]/u;
      const dm = degRe.exec(expr.slice(i));
      if (dm) {
        toks.push({ kind: "unit", text: dm[0] ?? "", pos: i });
        i += (dm[0] ?? "").length;
        continue;
      }
      i++;
      continue;
    }

    // Unknown character — skip
    i++;
  }

  toks.push({ kind: "eof", text: "", pos: i });
  return toks;
}

// ---------------------------------------------------------------------------
// Parser state
// ---------------------------------------------------------------------------

/**
 * Maximum recursive nesting depth for an expression (parenthesised groups and
 * function-call arguments). A deeply-nested adversarial input (e.g. 2000 open
 * parens) would otherwise recurse the descent parser past the JS call-stack
 * limit and throw a RangeError, escaping the parser's typed-error contract.
 *
 * Cap chosen well above any legitimate recipe expression; a recipe nested 256
 * levels deep is pathological, not a real cook's formula.
 *
 * Security: the grammar is a closed sandbox — every malformed input must yield a
 * TYPED parse error, never an uncaught host exception.
 */
const MAX_EXPR_DEPTH = 256;

interface ParseState {
  toks: Tok[];
  pos: number;
  errors: string[];
  /** Current recursive nesting depth (parens / call args). */
  depth: number;
  /** Set once the depth cap is hit so we report the typed error only once. */
  depthExceeded: boolean;
}

function peek(state: ParseState): Tok {
  return state.toks[state.pos] ?? { kind: "eof", text: "", pos: 0 };
}

function consume(state: ParseState): Tok {
  const tok = state.toks[state.pos] ?? { kind: "eof", text: "", pos: 0 };
  state.pos++;
  return tok;
}

function expect(state: ParseState, kind: TokKind): Tok | null {
  const tok = peek(state);
  if (tok.kind !== kind) {
    state.errors.push(`Expected ${kind}, got ${tok.kind} ("${tok.text}")`);
    return null;
  }
  return consume(state);
}

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------

export interface ExprParseResult {
  node: ExpressionNode | null;
  errors: string[];
  /** Number of characters consumed from the input (including leading `=`). */
  consumed: number;
}

/**
 * Parse an expression string.
 *
 * The input may or may not start with `=`. The `=` is stripped if present.
 * Returns the parsed ExpressionNode and any parse errors.
 */
export function parseExpression(raw: string): ExprParseResult {
  let input = raw.trim();
  if (input.startsWith("=")) input = input.slice(1).trim();

  if (input === "") {
    return { node: null, errors: ["Empty expression"], consumed: raw.length };
  }

  const toks = tokenize(input);
  const state: ParseState = { toks, pos: 0, errors: [], depth: 0, depthExceeded: false };

  const node = parseExpr(state);

  // Check for implicit multiplication: a number token immediately followed
  // by an ident token with no operator between them.
  // We scan the final token list for this pattern.
  for (let j = 0; j < toks.length - 1; j++) {
    const t = toks[j]!;
    const next = toks[j + 1]!;
    if (t.kind === "number" && next.kind === "ident") {
      // The number token ends at t.pos + t.text.length.
      // The ident starts at next.pos.
      // If the ident immediately follows (possibly with spaces stripped by tokenizer),
      // this is an implicit multiplication.
      state.errors.push(
        `Implicit multiplication is not allowed. Write \`2 * ${next.text}\` not \`2 ${next.text}\`.`,
      );
    }
  }

  // Check for reserved keywords used as identifiers (but NOT as function call names).
  // Function calls are: ident followed immediately by lparen.
  // e.g. cylinder(22cm, 5cm) is a valid function call — 'cylinder' is NOT an error there.
  // But 'cylinder: 400g' (labelled-line) would be flagged by the parser.
  for (let j = 0; j < toks.length; j++) {
    const tok = toks[j]!;
    const nextTok = toks[j + 1];
    if (tok.kind === "ident" && RESERVED_KEYWORDS.has(tok.text.toLowerCase())) {
      if (tok.text !== "where") {
        // Skip if immediately followed by lparen (function call context — valid use of stdlib names)
        if (nextTok?.kind === "lparen") continue;
        // Otherwise flag as reserved-keyword-as-identifier
        state.errors.push(
          `"${tok.text}" is a reserved keyword and cannot be used as a variable name.`,
        );
      }
    }
  }

  return {
    node,
    errors: state.errors,
    consumed: raw.length,
  };
}

// ---------------------------------------------------------------------------
// Exported helpers for tests and the labelled-line auto-detection path
// ---------------------------------------------------------------------------

/**
 * Check whether a reserved keyword is being used as a plain identifier.
 * Used by the binding parser to validate identifier names.
 *
 * extensibility-design.md §3.4.0 — reserved keywords.
 */
export function isReservedKeyword(name: string): boolean {
  return RESERVED_KEYWORDS.has(name.toLowerCase());
}

/**
 * Validate an identifier string per the L13 rules:
 *   - Non-empty after trim.
 *   - Characters: A-Za-z0-9 space (internal only), hyphen (mid-only), underscore.
 *   - No leading / trailing spaces.
 *   - No reserved keyword.
 *
 * Returns null on success, or an error code string on failure.
 */
export function validateIdentifier(
  name: string,
):
  | null
  | "E_IDENT_EMPTY"
  | "E_IDENT_LEADING_SPACE"
  | "E_IDENT_TRAILING_SPACE"
  | "E_RESERVED_KEYWORD" {
  if (name.length === 0) return "E_IDENT_EMPTY";
  if (name.startsWith(" ")) return "E_IDENT_LEADING_SPACE";
  if (name.endsWith(" ")) return "E_IDENT_TRAILING_SPACE";
  if (isReservedKeyword(name)) return "E_RESERVED_KEYWORD";
  return null;
}

// ---------------------------------------------------------------------------
// Grammar rules
// ---------------------------------------------------------------------------

function parseExpr(state: ParseState): ExpressionNode | null {
  // Depth guard: every parenthesised group and function-call argument re-enters
  // through parseExpr. Cap the recursive nesting so an adversarial deeply-nested
  // input yields a TYPED parse error instead of a RangeError (stack overflow).
  if (state.depth >= MAX_EXPR_DEPTH) {
    if (!state.depthExceeded) {
      state.depthExceeded = true;
      state.errors.push("Expression too deeply nested.");
    }
    return null;
  }
  state.depth++;
  const node = parseAddSub(state);
  state.depth--;
  return node;
}

function parseAddSub(state: ParseState): ExpressionNode | null {
  let left = parseMulDiv(state);
  if (left === null) return null;

  let tok = peek(state);
  while (tok.kind === "plus" || tok.kind === "minus") {
    consume(state);
    const op: BinaryOp = tok.kind === "plus" ? "+" : "-";
    const right = parseMulDiv(state);
    if (right === null) {
      state.errors.push(`Expected expression after '${op}'`);
      break;
    }
    const node: BinaryOpNode = { kind: "binary-op", op, left, right };
    left = node;
    tok = peek(state);
  }

  return left;
}

function parseMulDiv(state: ParseState): ExpressionNode | null {
  let left = parsePower(state);
  if (left === null) return null;

  let tok = peek(state);
  while (tok.kind === "star" || tok.kind === "slash") {
    consume(state);
    const op: BinaryOp = tok.kind === "star" ? "*" : "/";
    const right = parsePower(state);
    if (right === null) {
      state.errors.push(`Expected expression after '${op}'`);
      break;
    }
    const node: BinaryOpNode = { kind: "binary-op", op, left, right };
    left = node;
    tok = peek(state);
  }

  return left;
}

function parsePower(state: ParseState): ExpressionNode | null {
  const base = parseUnary(state);
  if (base === null) return null;

  if (peek(state).kind === "caret") {
    consume(state);
    const exp = parseUnary(state);
    if (exp === null) {
      state.errors.push("Expected expression after '^'");
      return base;
    }
    const node: BinaryOpNode = { kind: "binary-op", op: "^", left: base, right: exp };
    return node;
  }

  return base;
}

function parseUnary(state: ParseState): ExpressionNode | null {
  if (peek(state).kind === "minus") {
    // Guard chained unary-minus (`-----…-5`) against unbounded self-recursion —
    // same closed-sandbox typed-error contract as the parseExpr depth cap.
    if (state.depth >= MAX_EXPR_DEPTH) {
      if (!state.depthExceeded) {
        state.depthExceeded = true;
        state.errors.push("Expression too deeply nested.");
      }
      return null;
    }
    consume(state);
    state.depth++;
    const operand = parseUnary(state);
    state.depth--;
    if (operand === null) return null;
    const node: UnaryMinusNode = { kind: "unary-minus", operand };
    return node;
  }
  return parsePrimary(state);
}

function parsePrimary(state: ParseState): ExpressionNode | null {
  const tok = peek(state);

  // Grouped expression
  if (tok.kind === "lparen") {
    consume(state);
    const inner = parseExpr(state);
    expect(state, "rparen");
    return inner;
  }

  // Numeric literal
  if (tok.kind === "number") {
    // Check for implicit multiplication: number immediately followed by ident
    // (the tokenizer may have already placed them adjacently).
    // We record the error but continue parsing.
    consume(state);
    return parseLiteral(tok);
  }

  // @section.field reference
  if (tok.kind === "at-ident") {
    consume(state);
    const atName = tok.text; // e.g. "@ingredients"
    if (peek(state).kind === "dot") {
      consume(state); // consume .
      const field = peek(state);
      if (field.kind === "ident") {
        consume(state);
        const node: ReferenceNode = {
          kind: "reference",
          path: [atName, field.text],
        };
        return node;
      }
    }
    // Just @section with no .field — used in aggregations
    const node: ReferenceNode = { kind: "reference", path: [atName] };
    return node;
  }

  // ../ parent scope reference
  if (tok.kind === "dotdot-slash") {
    consume(state);
    const ident = expect(state, "ident");
    if (ident === null) return null;
    const node: ReferenceNode = {
      kind: "reference",
      path: ["../", ident.text],
    };
    return node;
  }

  // Identifier: could be variable reference, function call, or aggregation
  if (tok.kind === "ident") {
    consume(state);
    const name = tok.text;

    // Check for aggregation functions
    if (AGG_FNS.has(name) && peek(state).kind === "lparen") {
      return parseAggregation(state, name);
    }

    // Function call: ident(...)
    if (peek(state).kind === "lparen") {
      return parseFunctionCall(state, name);
    }

    // Scope path: ident/ident (e.g. Biga/flour, "Final Dough/flour")
    // Disambiguation rules:
    //   1. Only treat `ident/ident` as a scope path if the first ident starts
    //      with an uppercase letter (scope names are capitalized by
    //      convention: "Biga", "Final Dough", "Tier 1").
    //   2. The slash must be ADJACENT on both sides (no whitespace):
    //      `Biga/flour` is a scope path; `Water / Flour` is division.
    //      Without the adjacency rule, capitalisation silently changes the
    //      meaning of a spaced division (QA 2026-06-11 A4 parse layer —
    //      `=Water / Flour * 100` must divide, exactly like its lowercase
    //      control `=water / flour * 100`).
    const slashTok = peek(state);
    if (
      slashTok.kind === "slash" &&
      /^[A-Z]/u.test(name) &&
      slashTok.pos === tok.pos + name.length
    ) {
      // Could be a scope path — consume and check for an adjacent ident
      const savedPos = state.pos;
      consume(state); // slash
      const subTok = peek(state);
      if (subTok.kind === "ident" && subTok.pos === slashTok.pos + 1) {
        const subIdent = consume(state);
        const path: string[] = [name, subIdent.text];
        // Further segments — same both-sides-adjacent rule per segment
        let segEnd = subIdent.pos + subIdent.text.length;
        while (peek(state).kind === "slash" && peek(state).pos === segEnd) {
          const savedPos2 = state.pos;
          const segSlash = consume(state);
          const segTok = peek(state);
          if (segTok.kind === "ident" && segTok.pos === segSlash.pos + 1) {
            const seg = consume(state);
            path.push(seg.text);
            segEnd = seg.pos + seg.text.length;
          } else {
            state.pos = savedPos2;
            break;
          }
        }
        // Optional subfield
        let subField: string | undefined;
        if (peek(state).kind === "dot") {
          consume(state);
          if (peek(state).kind === "ident") {
            subField = consume(state).text;
          }
        }
        const node: ReferenceNode = {
          kind: "reference",
          path,
          ...(subField !== undefined ? { subField } : {}),
        };
        return node;
      } else {
        // Not a scope path — back up
        state.pos = savedPos;
      }
    }

    // Optional dot subfield access (e.g. flour.mass)
    let subField: string | undefined;
    if (peek(state).kind === "dot") {
      consume(state);
      if (peek(state).kind === "ident") {
        subField = consume(state).text;
      }
    }

    const node: ReferenceNode = {
      kind: "reference",
      path: [name],
      ...(subField !== undefined ? { subField } : {}),
    };
    return node;
  }

  state.errors.push(`Unexpected token: ${tok.kind} ("${tok.text}")`);
  return null;
}

// ---------------------------------------------------------------------------
// Literal parsing
// ---------------------------------------------------------------------------

function parseLiteral(tok: Tok): LiteralNode {
  // tok.text might be "400g" or "22cm" or "65%"
  const text = tok.text;
  const numRe = /^(\d+(?:\.\d+)?)(.*)/u;
  const m = numRe.exec(text);
  const numStr = m?.[1] ?? text;
  const unitStr = m?.[2] ?? "";
  const value = parseFloat(numStr);
  return { kind: "literal", value: isFinite(value) ? value : 0, unit: unitStr };
}

// ---------------------------------------------------------------------------
// Aggregation parsing: sum(@ingredients where kind=flour)
// ---------------------------------------------------------------------------

function parseAggregation(state: ParseState, fnName: string): ExpressionNode | null {
  const fn = fnName as AggregationFn;

  expect(state, "lparen");

  // Parse target: @section or @section.field
  const targetTok = peek(state);
  let target = "";
  let targetField: string | undefined;

  if (targetTok.kind === "at-ident") {
    consume(state);
    target = targetTok.text;
    if (peek(state).kind === "dot") {
      consume(state);
      if (peek(state).kind === "ident") {
        targetField = consume(state).text;
      }
    }
  } else if (targetTok.kind === "ident") {
    // Also allow bare ident without @
    consume(state);
    target = targetTok.text;
  } else {
    state.errors.push(
      `Expected @section reference as first arg of ${fnName}(), got ${targetTok.kind}`,
    );
    // Try to skip to closing paren
    while (peek(state).kind !== "rparen" && peek(state).kind !== "eof") consume(state);
    expect(state, "rparen");
    const node: AggregationNode = { kind: "aggregation", fn, target };
    return node;
  }

  // Additional targets (e.g. sum(@oven.time, @ferment.time))
  const additionalTargets: string[] = [];

  // Check for `where` clause or additional targets (comma separated)
  let filter: AggregationFilter | undefined;

  while (peek(state).kind === "comma" || peek(state).kind === "where") {
    if (peek(state).kind === "where") {
      consume(state); // consume 'where'
      filter = parseAggregationFilter(state);
      break;
    }
    if (peek(state).kind === "comma") {
      consume(state);
      const nextTok = peek(state);
      if (nextTok.kind === "at-ident") {
        consume(state);
        let addTarget = nextTok.text;
        if (peek(state).kind === "dot") {
          consume(state);
          if (peek(state).kind === "ident") {
            addTarget += "." + consume(state).text;
          }
        }
        additionalTargets.push(addTarget);
      } else if (nextTok.kind === "ident") {
        consume(state);
        additionalTargets.push(nextTok.text);
      } else {
        break;
      }
    }
  }

  expect(state, "rparen");

  const node: AggregationNode = {
    kind: "aggregation",
    fn,
    target,
    ...(targetField !== undefined ? { targetField } : {}),
    ...(filter !== undefined ? { filter } : {}),
    ...(additionalTargets.length > 0 ? { additionalTargets } : {}),
  };
  return node;
}

/**
 * Parse a `where` filter clause: `field=value`, `field != value`, `temp < 175°F`, etc.
 */
function parseAggregationFilter(state: ParseState): AggregationFilter | undefined {
  const fieldTok = peek(state);
  if (fieldTok.kind !== "ident") {
    state.errors.push(`Expected field name in where clause, got ${fieldTok.kind}`);
    return undefined;
  }
  consume(state);
  const field = fieldTok.text;

  // Operator
  const opTok = peek(state);
  let op: AggregationFilter["op"] | null = null;
  if (opTok.kind === "eq") {
    consume(state);
    op = "=";
  } else if (opTok.kind === "neq") {
    consume(state);
    op = "!=";
  } else if (opTok.kind === "lt") {
    consume(state);
    op = "<";
  } else if (opTok.kind === "lte") {
    consume(state);
    op = "<=";
  } else if (opTok.kind === "gt") {
    consume(state);
    op = ">";
  } else if (opTok.kind === "gte") {
    consume(state);
    op = ">=";
  } else {
    state.errors.push(`Expected comparison operator in where clause, got ${opTok.kind}`);
    return undefined;
  }

  // Value: collect tokens until comma or rparen
  const valueToks: string[] = [];
  while (
    peek(state).kind !== "rparen" &&
    peek(state).kind !== "comma" &&
    peek(state).kind !== "eof" &&
    peek(state).kind !== "where"
  ) {
    valueToks.push(consume(state).text);
  }

  return { field, op, value: valueToks.join("") };
}

// ---------------------------------------------------------------------------
// Function call parsing: name(arg1, arg2, ...)
// ---------------------------------------------------------------------------

function parseFunctionCall(state: ParseState, name: string): ExpressionNode | null {
  expect(state, "lparen");
  const args: ExpressionNode[] = [];

  if (peek(state).kind !== "rparen") {
    const firstArg = parseExpr(state);
    if (firstArg !== null) args.push(firstArg);

    while (peek(state).kind === "comma") {
      consume(state);
      const arg = parseExpr(state);
      if (arg !== null) args.push(arg);
    }
  }

  expect(state, "rparen");

  const node: FunctionCallNode = { kind: "function-call", name, args };
  return node;
}
